/**
 * ============================================================
 * 账单管理页面 (Bills)
 * ============================================================
 * 功能说明：
 *   - 账单列表（按月份/经纪人/状态筛选）
 *   - 统计卡片（总账单/待确认/待付款/已付款）
 *   - 确认按钮（经纪人角色可操作）
 *   - 付款按钮（财务角色可操作）
 *   - 发票上传（图片/PDF）
 *   - 发票预览弹窗
 *   - Toast通知
 *
 * 原系统新增功能
 * ============================================================
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { billApi, brokerApi, formulaApi, settlementApi } from '../api';
import { useAuth } from '../auth';
import type { Bill, Broker, BillStats, Formula, SettlementData, BillAggregatedData } from '../types';
import {
  Receipt, RefreshCw, CheckCircle, DollarSign, FileImage, FileText,
  X, Upload, Filter, CalendarDays, Users, PlusCircle, MinusCircle, Calculator, Trash2, XCircle, AlertCircle
} from 'lucide-react';

export default function Bills() {
  const { user, isAdmin } = useAuth();

  // ========== 状态管理 ==========

  /** 账单列表 */
  const [bills, setBills] = useState<Bill[]>([]);
  /** 经纪人列表（用于筛选） */
  const [brokers, setBrokers] = useState<Broker[]>([]);
  /** 公式列表 */
  const [formulas, setFormulas] = useState<Formula[]>([]);
  /** 统计 */
  const [stats, setStats] = useState<BillStats>({ total: 0, pending: 0, confirmed: 0, paid: 0 });
  /** 加载状态 */
  const [loading, setLoading] = useState(true);
  /** 处理中（确认/付款） */
  const [processingId, setProcessingId] = useState<number | null>(null);
  /** 发票上传中 */
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  /** 预览弹窗 */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** 预览类型 */
  const [previewType, setPreviewType] = useState<'image' | 'pdf'>('image');
  /** 文件输入ref */
  const fileRef = useRef<HTMLInputElement>(null);
  /** Toast */
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  /** 发票提醒弹窗 */
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  /** 驳回弹窗 */
  const [showRejectModal, setShowRejectModal] = useState(false);
  /** 当前驳回的账单ID */
  const [rejectingBillId, setRejectingBillId] = useState<number | null>(null);
  /** 驳回原因 */
  const [rejectReason, setRejectReason] = useState('');
  /** 展开的账单详情ID */
  const [expandedBillId, setExpandedBillId] = useState<number | null>(null);
  /** 账单聚合数据 */
  const [aggregatedData, setAggregatedData] = useState<Record<number, BillAggregatedData>>({});
  /** 可用月份列表 */
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  /** 分页状态 */
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  // ========== 角色判断 ==========
  // 兼容大小写和中文角色名
  const isBroker = user?.role?.toLowerCase() === 'broker' || user?.role === '经纪人';
  const isFinance = user?.role?.toLowerCase() === 'finance' || user?.role === '财务';
  // 判断当前用户是否为企业用户
  const isEnterprise = (user as any)?.card_type === '企业';

  // ========== 辅助函数 ==========
  /** 获取上月月份，格式为YYYYMM */
  const getLastMonth = () => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}${month}`;
  };

  /** 筛选条件 */
  const [filter, setFilter] = useState({ 
    brokerId: '', 
    month: '',
    status: '' 
  });

  // 用户信息加载完成后更新筛选条件
  useEffect(() => {
    if (user) {
      const newBrokerId = (isAdmin || isFinance) ? '' : ((user.bound_broker_id ? String(user.bound_broker_id) : '') || '');
      setFilter(prev => ({ ...prev, brokerId: newBrokerId }));
    }
  }, [user, isAdmin, isFinance]);

  // ========== Toast 辅助 ==========
  const showSuccess = useCallback((message: string) => { setToast({ message, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((message: string) => { setToast({ message, type: 'error' }); setTimeout(() => setToast(null), 3000); }, []);

  // ========== 生命周期 ==========
  // 页面加载时执行初始化
  useEffect(() => { loadInit(); }, []);

  // 筛选条件变化时自动重新生成账单
  useEffect(() => {
    if (!loading) {
      setPagination(prev => ({ ...prev, page: 1 }));
      handleAutoGenerate();
    }
  }, [filter.month, filter.brokerId, filter.status]);

  // 分页变化时重新加载账单
  useEffect(() => {
    if (!loading) {
      loadBills();
    }
  }, [pagination.page]);

  const loadInit = async () => {
    try {
      const monthsData = await billApi.getAvailableMonths().catch(() => ({ months: [] }));
      setAvailableMonths(monthsData.months || []);
      
      const [b, f] = await Promise.all([
        brokerApi.list().catch(() => []),
        formulaApi.list().catch(() => []),
      ]);
      
      console.log('[loadInit] 获取到 brokers:', b);
      console.log('[loadInit] 获取到 formulas:', f);
      
      setBrokers(b);
      setFormulas(f);
      
      // 使用获取到的数据直接计算，而不是等待状态更新
      await loadBills(b, f);
    } catch (error) {
      console.error('初始化失败:', error);
    } finally { setLoading(false); }
  };

  /** 自动生成账单（根据当前筛选条件） */
  const handleAutoGenerate = async () => {
    try {
      if (isAdmin || isFinance) {
        await billApi.generateFromSettlements(filter.month || undefined).catch(() => {});
      } else {
        const userBoundBrokerName = user?.bound_broker_name;
        if (userBoundBrokerName) {
          await billApi.generateFromSettlements(filter.month || undefined).catch(() => {});
        }
      }
      
      await loadBills();
    } catch (error) {
      console.error('自动生成账单失败:', error);
    }
  };

  /** 计算账单聚合数据 */
  const calculateBillAggregation = async (bill: Bill, useBrokers?: Broker[], useFormulas?: Formula[]) => {
    try {
      // 使用传入的参数或状态数据
      const currentBrokers = useBrokers || brokers;
      const currentFormulas = useFormulas || formulas;
      
      const brokerId = bill.brokerId || bill.broker_id;
      const brokerName = bill.brokerRealName || bill.brokerName || (bill as any).broker_name;
      
      console.log(`[账单计算] 开始计算账单 ${bill.id}，经纪人: ${brokerName}，brokerId: ${brokerId} (类型: ${typeof brokerId})`);
      console.log(`[账单计算] 当前 brokers 列表长度:`, currentBrokers.length);
      console.log(`[账单计算] 当前 brokers 列表:`, currentBrokers.map(b => ({ id: b.id, name: b.name, formulaId: b.formulaId || b.formula_id })));
      
      // 使用宽松相等比较，处理数字和字符串类型不匹配的问题
      const broker = currentBrokers.find(b => Number(b.id) === Number(brokerId));
      if (!broker) {
        console.log(`[账单计算] 未找到经纪人，brokerId: ${brokerId}`);
        return;
      }
      
      console.log(`[账单计算] 找到经纪人: ${broker.name}，formulaId: ${broker.formulaId || broker.formula_id}`);
      
      const formulaId = broker.formulaId || broker.formula_id;
      console.log(`[账单计算] 当前 formulas 列表长度:`, currentFormulas.length);
      console.log(`[账单计算] 当前 formulas 列表:`, currentFormulas.map(f => ({ id: f.id, name: f.name })));
      
      // 使用宽松相等比较，处理数字和字符串类型不匹配的问题
      const formula = currentFormulas.find(f => Number(f.id) === Number(formulaId));
      if (!formula) {
        console.log(`[账单计算] 未找到公式，formulaId: ${formulaId}`);
        return;
      }
      
      console.log(`[账单计算] 找到公式: ${formula.name}`);
      
      const testResult = await formulaApi.test(
        formula.expression,
        undefined,
        broker.name,
        bill.settlementMonth
      );
      
      const fieldResults = testResult.fieldResults || {};
      const shareDetails: Record<string, number> = {};
      
      // 提取公式中所有的字段标签（匹配所有 [字段名] 模式）
      const allFields: string[] = [];
      if (formula.expression) {
        try {
          // 匹配所有 [字段名] 模式
          const fieldMatches = formula.expression.match(/\[([^\]]+)\]/g) || [];
          for (const match of fieldMatches) {
            const fieldName = match.slice(1, -1); // 去掉方括号
            if (!allFields.includes(fieldName)) {
              allFields.push(fieldName);
            }
          }
          
          console.log(`[账单计算] 提取到的所有字段:`, allFields);
          
          // 为每个字段计算分成值（如果有对应的数值）
          for (const fieldName of allFields) {
            const fieldValue = fieldResults[fieldName] || 0;
            // 尝试查找该字段在公式中乘以的系数
            const coefficientMatch = formula.expression.match(new RegExp(`\\[${fieldName}\\]\\s*\\*\\s*(\\d+\\.?\\d*)`));
            if (coefficientMatch) {
              const rate = parseFloat(coefficientMatch[1]);
              shareDetails[`${fieldName}`] = fieldValue * rate;
            } else {
              // 如果没有乘系数，直接使用字段值
              shareDetails[`${fieldName}`] = fieldValue;
            }
          }
        } catch (error) {
          console.error('解析公式失败:', error);
        }
      }
      
      console.log(`[账单计算] shareDetails:`, shareDetails);
      
      setAggregatedData(prev => ({
        ...prev,
        [bill.id]: {
          formula,
          fieldValues: fieldResults,
          shareDetails,
          totalAmount: testResult.result || 0,
          allFields // 保存所有字段列表
        }
      }));
    } catch (error) {
      console.error('计算账单聚合数据失败:', error);
    }
  };

  /** 加载账单列表 */
  const loadBills = async (tempBrokers?: Broker[], tempFormulas?: Formula[], tempFilter?: Partial<FilterState>) => {
    setLoading(true);
    try {
      const currentFilter = tempFilter || filter;
      const params: { brokerId?: number; month?: string; status?: string; page?: number; pageSize?: number } = {};
      // 管理员/财务用户可以选择任意经纪人，普通用户只能查看自己绑定经纪人的账单
      if (currentFilter.brokerId) {
        params.brokerId = Number(currentFilter.brokerId);
      }
      if (currentFilter.month) params.month = currentFilter.month;
      if (currentFilter.status) params.status = currentFilter.status;
      params.page = pagination.page;
      params.pageSize = pagination.pageSize;
      
      // 构建统计查询参数，统计卡片始终显示经纪人名下所有账单的各状态数量
      // 注意：统计查询不传递 status 参数，确保获取所有状态的统计数据
      const statsParams: { month?: string; brokerId?: number } = {};
      if (currentFilter.month) statsParams.month = currentFilter.month;
      // 如果选择了经纪人（管理员/财务可选择，普通用户默认绑定），传递 brokerId
      if (currentFilter.brokerId) {
        statsParams.brokerId = Number(currentFilter.brokerId);
      }
      
      const [result, statsResult] = await Promise.all([
        billApi.list(params),
        billApi.stats(statsParams)
      ]);
      
      const billData = result?.data || [];
      const totalCount = result?.total || 0;
      
      setBills(billData);
      setPagination(prev => ({ ...prev, total: totalCount }));
      
      // 使用后端返回的统计数据，而不是从当前分页计算
      setStats({
        total: statsResult?.total || 0,
        pending: statsResult?.pending || 0,
        confirmed: statsResult?.confirmed || 0,
        paid: statsResult?.paid || 0
      });
      
      setAggregatedData({});
      
      // 自动计算所有账单的金额（使用实时公式）
      // 如果提供了临时数据则使用临时数据，否则使用状态
      const useBrokers = tempBrokers || brokers;
      const useFormulas = tempFormulas || formulas;
      
      console.log('[loadBills] 使用的 brokers:', useBrokers);
      console.log('[loadBills] 使用的 formulas:', useFormulas);
      
      for (const bill of billData) {
        await calculateBillAggregation(bill, useBrokers, useFormulas);
      }
    } catch (error) {
      console.error('加载账单列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // ========== 操作 ==========

  /** 确认账单 */
  const handleConfirm = async (billId: number) => {
    // 获取当前账单
    const bill = bills.find(b => b.id === billId);
    if (!bill) return;
    
    // 企业用户必须先上传发票才能确认金额
    if (isEnterprise && !bill.invoiceUrl) {
      setShowInvoiceModal(true);
      return;
    }
    
    if (!confirm('确定确认此账单金额吗？')) return;
    setProcessingId(billId);
    try { await billApi.confirm(billId); showSuccess('确认金额成功'); await loadBills(); }
    catch (err: any) { showError(err.message || '确认失败'); }
    finally { setProcessingId(null); }
  };

  /** 打款 */
  const handlePay = async (billId: number) => {
    if (!confirm('确定要打款吗？')) return;
    setProcessingId(billId);
    try { await billApi.pay(billId); showSuccess('打款成功'); await loadBills(); }
    catch (err: any) { showError(err.message || '打款失败'); }
    finally { setProcessingId(null); }
  };

  /** 打开驳回弹窗 */
  const openRejectModal = (billId: number) => {
    setRejectingBillId(billId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  /** 驳回账单 */
  const handleReject = async () => {
    if (!rejectingBillId) return;
    if (!rejectReason.trim()) {
      showError('请填写驳回原因');
      return;
    }
    setProcessingId(rejectingBillId);
    try { 
      await billApi.reject(rejectingBillId, rejectReason); 
      showSuccess('驳回成功'); 
      await loadBills(); 
      setShowRejectModal(false);
      setRejectingBillId(null);
      setRejectReason('');
    }
    catch (err: any) { showError(err.message || '驳回失败'); }
    finally { setProcessingId(null); }
  };

  /** 上传发票 */
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, billId: number) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(billId);
    try {
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const res = await billApi.upload({
        fileName: file.name,
        fileType: file.type,
        base64Data: base64Data
      });
      await billApi.invoice(billId, res.url, res.fileType);
      showSuccess('发票上传成功');
      await loadBills();
    } catch (err: any) { showError(err.message || '上传失败'); }
    finally { setUploadingId(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  /** 删除发票 */
  const handleDeleteInvoice = async (billId: number) => {
    if (!confirm('确定要删除该发票吗？')) return;
    try {
      await billApi.deleteInvoice(billId);
      showSuccess('发票删除成功');
      await loadBills();
    } catch (err: any) { 
      showError(err.message || '删除失败'); 
    }
  };

  /** 打开发票预览 */
  const openPreview = (url: string, type: string) => {
    // 如果是相对路径，使用当前域名
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    console.log('[发票预览调试] 原始URL:', url);
    console.log('[发票预览调试] 完整URL:', fullUrl);
    console.log('[发票预览调试] 文件类型:', type);
    console.log('[发票预览调试] 当前域名:', window.location.origin);
    setPreviewUrl(fullUrl);
    setPreviewType(type === 'pdf' ? 'pdf' : 'image');
  };

  /** 切换账单详情展开/收起 */
  const toggleBillDetails = async (bill: Bill) => {
    if (expandedBillId === bill.id) {
      setExpandedBillId(null);
    } else {
      setExpandedBillId(bill.id);
      if (!aggregatedData[bill.id]) {
        await calculateBillAggregation(bill);
      }
    }
  };

  // ========== 渲染 ==========

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Receipt className="w-6 h-6 text-[#F59E0B]" />
          账单管理
        </h1>
        <button onClick={handleAutoGenerate}
          className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-all">
          <RefreshCw className="w-4 h-4" /> 刷新
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: '账单总数', value: stats.total, color: 'text-blue-400', icon: Receipt, status: '' },
          { label: '待确认', value: stats.pending, color: 'text-amber-400', icon: CheckCircle, status: 'pending' },
          { label: '已确认', value: stats.confirmed, color: 'text-yellow-400', icon: CheckCircle, status: 'confirmed' },
          { label: '已打款', value: stats.paid, color: 'text-emerald-400', icon: DollarSign, status: 'paid' },
        ].map((s, i) => (
          <div 
            key={i} 
            className={`bg-[#1E293B] border rounded-xl p-4 cursor-pointer transition-all hover:border-[#3B82F6] hover:bg-[#0F172A] ${
              filter.status === s.status ? 'border-[#3B82F6] bg-[#0F172A]' : 'border-[#334155]'
            }`}
            onClick={() => {
              const newStatus = s.status;
              const newFilter = { ...filter, status: newStatus };
              setFilter(newFilter);
              loadBills(undefined, undefined, newFilter);
            }}
          >
            <div className="flex items-center gap-2">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-xs text-[#94A3B8]">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 筛选栏 */}
      <div className="flex flex-wrap gap-3 bg-[#1E293B] border border-[#334155] rounded-xl p-4">
        {/* 经纪人显示 */}
        <div className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm flex items-center">
          {isAdmin || isFinance ? (
            <select value={filter.brokerId} onChange={e => setFilter({ ...filter, brokerId: e.target.value })}
              className="bg-[#0F172A] text-[#F1F5F9] focus:outline-none h-full px-2">
              <option value="" className="bg-[#0F172A] text-[#F1F5F9]">全部经纪人</option>
              {brokers.map(b => <option key={b.id} value={b.id} className="bg-[#0F172A] text-[#F1F5F9]">{b.name}</option>)}
            </select>
          ) : (
            <span className="text-[#F59E0B] font-medium">{user?.bound_broker_name || '未绑定经纪人'}</span>
          )}
        </div>
        
        <select value={filter.month} onChange={e => setFilter({ ...filter, month: e.target.value })}
          className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
          <option value="" className="bg-[#0F172A] text-[#F1F5F9]">全部月份</option>
          {availableMonths.map(month => (
            <option key={month} value={month} className="bg-[#0F172A] text-[#F1F5F9]">{month}</option>
          ))}
        </select>

        <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}
          className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
          <option value="" className="bg-[#0F172A] text-[#F1F5F9]">全部状态</option>
          <option value="pending" className="bg-[#0F172A] text-[#F1F5F9]">待确认</option>
          <option value="confirmed" className="bg-[#0F172A] text-[#F1F5F9]">已确认金额</option>
          <option value="paid" className="bg-[#0F172A] text-[#F1F5F9]">已打款</option>
        </select>
      </div>

      {/* 表格 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : bills.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]">暂无账单</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#334155] bg-[#0F172A]">
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">经纪人</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">月份</th>
                  <th className="px-4 py-3 text-right text-xs text-[#F59E0B] font-medium">分成金额</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">发票</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">状态</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">金额确认人</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">银行账号</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">付款人</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">操作时间</th>
                  <th className="px-4 py-3 text-right text-xs text-[#94A3B8] font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#334155]/50">
                {bills.map(bill => {
                  const isExpanded = expandedBillId === bill.id;
                  const billData = aggregatedData[bill.id];
                  const status = bill.status || (bill as any).status || 'pending';
                  // 判断用户是否为企业用户（通过card_type字段）
                  const isEnterprise = (user as any)?.card_type === '企业';
                  
                  return (
                    <React.Fragment key={bill.id}>
                      <tr className="hover:bg-[#334155]/20 transition-colors">
                        <td className="px-4 py-3 font-medium whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleBillDetails(bill)}
                              className="text-[#94A3B8] hover:text-white transition-colors">
                              {isExpanded ? <MinusCircle className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
                            </button>
                            {bill.brokerRealName || bill.brokerName || (bill as any).broker_name}
                            {isEnterprise && <span className="ml-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">企业</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{bill.settlementMonth}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {(() => {
                            const billData = aggregatedData[bill.id];
                            // 优先显示实时计算的金额，否则显示数据库中的金额
                            if (billData && billData.totalAmount > 0) {
                              return <span className="font-bold text-[#F59E0B]">¥{billData.totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                            } else if (bill.shareAmount !== null && bill.shareAmount !== undefined) {
                              return <span className="font-bold text-[#F59E0B]">¥{bill.shareAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                            } else {
                              return <span className="text-[#EF4444] text-sm">未绑定分成公式</span>;
                            }
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {bill.invoiceUrl ? (
                            <div className="flex flex-col items-start gap-1">
                              <button onClick={() => openPreview(bill.invoiceUrl!, bill.invoiceFileType || 'image')}
                                className="flex items-center gap-1 text-[#3B82F6] hover:underline">
                                {bill.invoiceFileType === 'pdf' ? (
                                  <FileText className="w-8 h-8 text-[#3B82F6]" />
                                ) : (
                                  <div className="w-10 h-10 bg-[#0F172A] rounded border border-[#334155] overflow-hidden flex items-center justify-center cursor-pointer hover:border-[#3B82F6]">
                                    <img src={bill.invoiceUrl} alt="发票缩略图" className="w-full h-full object-cover" />
                                  </div>
                                )}
                                <span className="text-xs text-[#3B82F6]">点击查看</span>
                              </button>
                              {/* 删除发票按钮：企业用户可见，待确认状态（包括被驳回的）可删除重新上传 */}
                              {!isAdmin && !isFinance && user?.bound_broker_name && isEnterprise && (status === 'pending' || (bill as any).rejectReason) && (
                                <button onClick={() => handleDeleteInvoice(bill.id)}
                                  className="h-5 px-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-xs flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" /> 删除发票
                                </button>
                              )}
                            </div>
                          ) : (
                            // 上传发票只对企业用户可见可操作
                            !isAdmin && !isFinance && user?.bound_broker_name && isEnterprise && (
                              <div className="flex items-center gap-1">
                                <input key={bill.id} type="file" accept="image/*,.pdf" className="hidden"
                                  onChange={e => bill.id && handleFileSelect(e, bill.id)} />
                                <button onClick={(e) => { 
                                  setUploadingId(bill.id); 
                                  const target = e.currentTarget;
                                  const input = target.previousElementSibling as HTMLInputElement;
                                  input?.click();
                                }}
                                  disabled={uploadingId === bill.id}
                                  className="h-7 px-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                                  <Upload className="w-3 h-3" /> 
                                  {uploadingId === bill.id ? '上传中...' : '上传发票'}
                                </button>
                              </div>
                            )
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              status === 'confirmed' ? 'bg-amber-500/10 text-amber-400' : 
                              status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : 
                              'bg-gray-500/10 text-gray-400'
                            }`}>
                              {status === 'confirmed' ? '已确认金额' : status === 'paid' ? '已打款' : '待确认'}
                            </span>
                            {/* 显示驳回原因 */}
                            {status === 'pending' && (bill as any).rejectReason && (
                              <div className="text-xs text-red-400 flex items-start gap-1">
                                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">驳回原因：{(bill as any).rejectReason}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#94A3B8] text-xs whitespace-nowrap">{(bill as any).confirmOperator || '-'}</td>
                        <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono whitespace-nowrap">
                          {(bill as any).brokerCardType === '企业' 
                            ? ((bill as any).brokerBankAccountPublic || '-')
                            : (bill.brokerBankCard || (bill as any).bank_card || '-')}
                        </td>
                        <td className="px-4 py-3 text-[#94A3B8] text-xs whitespace-nowrap">{(bill as any).payOperator || '-'}</td>
                        <td className="px-4 py-3 text-[#94A3B8] text-xs whitespace-nowrap">
                          {(() => {
                            // 兼容驼峰和下划线两种命名格式
                            const paidAt = (bill as any).paidAt || (bill as any).paid_at;
                            const confirmedAt = (bill as any).confirmedAt || (bill as any).confirmed_at;
                            if (paidAt) {
                              const date = new Date(paidAt);
                              return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                            } else if (confirmedAt) {
                              const date = new Date(confirmedAt);
                              return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                            }
                            return '-';
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {/* 确认金额按钮：经纪人、用户可见（绑定了经纪人的用户），仅待确认状态显示 */}
                            {status === 'pending' && !isAdmin && !isFinance && user?.bound_broker_name && (
                              <button onClick={() => handleConfirm(bill.id)}
                                disabled={processingId === bill.id}
                                className="h-6 px-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded text-xs flex items-center gap-1 disabled:opacity-50">
                                    <CheckCircle className="w-3 h-3" /> 确认金额
                              </button>
                            )}
                            {/* 打款按钮：财务管理员可见 */}
                            {status === 'confirmed' && (isFinance || isAdmin) && (
                              <button onClick={() => handlePay(bill.id)}
                                disabled={processingId === bill.id}
                                className="h-6 px-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded text-xs flex items-center gap-1 disabled:opacity-50">
                                    <DollarSign className="w-3 h-3" /> 打款
                              </button>
                            )}
                            {/* 驳回按钮：管理员和财务可见 */}
                            {status === 'confirmed' && (isFinance || isAdmin) && (
                              <button onClick={() => openRejectModal(bill.id)}
                                disabled={processingId === bill.id}
                                className="h-6 px-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-xs flex items-center gap-1 disabled:opacity-50">
                                    <XCircle className="w-3 h-3" /> 驳回
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* 账单详情行 */}
                      {isExpanded && (
                        <tr className="bg-[#1E293B]">
                          <td colSpan={10} className="px-6 py-4">
                            <div className="space-y-4">
                              {/* 未绑定公式提示 */}
                              {!billData?.formula && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                  <div className="text-red-400 text-sm">该经纪人未绑定分成公式，无法计算分成金额</div>
                                </div>
                              )}
                              
                              {/* 公式信息和计算结果合并显示 */}
                              {billData?.formula && (
                                <div className="bg-[#0F172A] rounded-lg p-4">
                                  {/* 公式名称 */}
                                  <div className="flex items-center gap-2 mb-3">
                                    <Calculator className="w-4 h-4 text-[#F59E0B]" />
                                    <span className="text-sm font-medium text-[#F59E0B]">{billData.formula.name}</span>
                                  </div>
                                  {/* 公式表达式 */}
                                  <div className="text-sm text-[#94A3B8] mb-3">
                                    {billData.formula.expression}
                                  </div>
                                  {/* 计算结果 - 显示所有公式字段及其计算值 */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {Object.entries(billData.shareDetails || {}).map(([fieldName, amount], index) => {
                                      return (
                                        <React.Fragment key={fieldName}>
                                          <span className="text-sm text-[#94A3B8]">{fieldName}：</span>
                                          <span className="text-sm font-medium text-[#10B981]">¥{amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          {index < Object.keys(billData.shareDetails || {}).length - 1 && (
                                            <span className="text-[#64748B] mx-1">+</span>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                    <span className="text-[#64748B] mx-1">=</span>
                                    <span className="text-sm font-medium">合计：</span>
                                    <span className="text-lg font-bold text-[#F59E0B]">¥{(billData?.totalAmount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* 分页 */}
        {!loading && bills.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 bg-[#0F172A] border-t border-[#334155]">
            <span className="text-sm text-[#94A3B8]">
              共 {pagination.total} 条记录，当前第 {pagination.page} 页
            </span>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => pagination.page > 1 && setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 bg-[#334155] hover:bg-[#475569] disabled:bg-[#1E293B] disabled:text-[#64748B] rounded text-sm transition-colors"
              >
                上一页
              </button>
              <span className="text-sm text-[#94A3B8]">
                {pagination.page} / {Math.ceil(pagination.total / pagination.pageSize)}
              </span>
              <button 
                onClick={() => pagination.page < Math.ceil(pagination.total / pagination.pageSize) && setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                className="px-3 py-1 bg-[#334155] hover:bg-[#475569] disabled:bg-[#1E293B] disabled:text-[#64748B] rounded text-sm transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 发票预览弹窗 */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPreviewUrl(null)}>
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-4 max-w-4xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">发票预览</h3>
              <button onClick={() => setPreviewUrl(null)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {previewType === 'pdf' ? (
              <iframe src={previewUrl} className="w-[600px] h-[700px] border-0" />
            ) : (
              <img src={previewUrl} alt="发票" className="max-w-full max-h-[70vh] rounded" />
            )}
          </div>
        </div>
      )}

      {/* 发票提醒弹窗 */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInvoiceModal(false)}>
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">请先上传发票</h3>
              <p className="text-[#94A3B8] mb-6">
                企业用户需要先上传发票才能确认金额，请在发票列点击"上传发票"按钮。
              </p>
              <button 
                onClick={() => setShowInvoiceModal(false)}
                className="w-full h-10 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg font-medium transition-colors"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 驳回弹窗 */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowRejectModal(false)}>
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">驳回账单</h3>
              <button onClick={() => setShowRejectModal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[#94A3B8] mb-4">
              请填写驳回原因，此原因将对账单所属用户可见。
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="请输入驳回原因..."
              className="w-full h-24 px-3 py-2 bg-[#0F172A] border border-[#334155] rounded-lg text-white placeholder-[#64748B] resize-none focus:outline-none focus:border-[#3B82F6]"
            />
            <div className="flex gap-3 mt-4">
              <button 
                onClick={() => setShowRejectModal(false)}
                className="flex-1 h-10 bg-[#334155] hover:bg-[#475569] text-white rounded-lg font-medium transition-colors"
              >
                取消
              </button>
              <button 
                onClick={handleReject}
                disabled={processingId !== null}
                className="flex-1 h-10 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                确认驳回
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}